// Composants partagés des écrans serveur et caisse :
// détail d'une addition + prise de commande rapide.

import React, { useState, useEffect } from 'react';
import { API_URL } from '../utils/api';
import { staffFetch } from '../utils/staffApi';
import { ORDER_STATUS, euro, timeHM } from './staffCommon';

const CATEGORY_ORDER = ['Bouchées', 'Burgers', 'Plats', 'A partager', 'Enfants', 'Boissons', 'Desserts'];

// --- Prise de commande rapide (grille de produits, gros boutons) ---

export function ProductPicker({ title, onClose, onSend }) {
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState(null);
  const [lines, setLines] = useState([]); // {id, name, price, quantity, notes, noteOpen}
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${API_URL}/products`)
      .then(r => r.json())
      .then(list => {
        const avail = list.filter(p => p.available);
        setProducts(avail);
        const cats = categoriesOf(avail);
        setCategory(cats[0] || null);
      })
      .catch(() => setError('Impossible de charger la carte'));
  }, []);

  const categoriesOf = (list) => {
    const present = [...new Set(list.map(p => p.category))];
    return [
      ...CATEGORY_ORDER.filter(c => present.includes(c)),
      ...present.filter(c => !CATEGORY_ORDER.includes(c))
    ];
  };

  const cats = categoriesOf(products);
  const shown = products.filter(p => p.category === category);
  const countFor = (id) => lines.filter(l => l.id === id).reduce((s, l) => s + l.quantity, 0);

  const [optionsFor, setOptionsFor] = useState(null);

  // option = {name, price} choisie, ou null (nature)
  const add = (p, option = null) => {
    const optName = option ? option.name : null;
    setLines(prev => {
      const existing = prev.find(l => l.id === p.id && !l.notes && (l.option || null) === optName);
      if (existing) {
        return prev.map(l => l === existing ? { ...l, quantity: l.quantity + 1 } : l);
      }
      return [...prev, {
        id: p.id,
        name: option ? `${p.name} — ${option.name}` : p.name,
        price: parseFloat(p.price) + (option ? parseFloat(option.price) || 0 : 0),
        option: optName,
        quantity: 1, notes: '', noteOpen: false, uid: Math.random()
      }];
    });
    setOptionsFor(null);
  };

  const handleTap = (p) => {
    if (Array.isArray(p.options) && p.options.length > 0) setOptionsFor(p);
    else add(p);
  };

  const setQty = (uid, q) => {
    setLines(prev => q <= 0 ? prev.filter(l => l.uid !== uid) : prev.map(l => l.uid === uid ? { ...l, quantity: q } : l));
  };

  const total = lines.reduce((s, l) => s + parseFloat(l.price) * l.quantity, 0);
  const count = lines.reduce((s, l) => s + l.quantity, 0);

  const send = async () => {
    if (busy || lines.length === 0) return;
    setBusy(true);
    setError('');
    try {
      await onSend(lines.map(l => ({
        id: l.id, quantity: l.quantity,
        option: l.option || undefined,
        notes: l.notes || undefined
      })));
    } catch (e) {
      setError(e.message || 'Envoi impossible');
      setBusy(false);
    }
  };

  return (
    <div className="picker-overlay">
      <div className="staff-topbar">
        <h1>{title}</h1>
        <button className="staff-logout" onClick={onClose}>Fermer</button>
      </div>

      <div className="picker-cats staff-chips">
        {cats.map(c => (
          <button key={c} className={`staff-chip ${category === c ? 'on' : ''}`} onClick={() => setCategory(c)}>
            {c}
          </button>
        ))}
      </div>

      <div className="picker-grid">
        {shown.map(p => {
          const n = countFor(p.id);
          const hasOpts = Array.isArray(p.options) && p.options.length > 0;
          return (
            <button key={p.id} className="picker-prod" onClick={() => handleTap(p)}>
              {n > 0 && <span className="picker-prod-count">{n}</span>}
              <span className="picker-prod-name">{p.name}{hasOpts ? ' ▾' : ''}</span>
              <span className="picker-prod-price">{euro(p.price)}</span>
            </button>
          );
        })}
      </div>

      {lines.length > 0 && (
        <div className="picker-cart">
          {lines.map(l => (
            <div key={l.uid}>
              <div className="picker-line">
                <div className="picker-line-qty">
                  <button onClick={() => setQty(l.uid, l.quantity - 1)}>−</button>
                  <span>{l.quantity}</span>
                  <button onClick={() => setQty(l.uid, l.quantity + 1)}>+</button>
                </div>
                <span className="picker-line-name">{l.name}</span>
                <button
                  className="staff-logout"
                  style={{ background: 'none', border: 'none', color: l.notes ? '#C29A63' : '#6c675f', cursor: 'pointer', fontSize: 17 }}
                  onClick={() => setLines(prev => prev.map(x => x.uid === l.uid ? { ...x, noteOpen: !x.noteOpen } : x))}
                  title="Note pour la cuisine"
                >
                  ✎
                </button>
                <span style={{ whiteSpace: 'nowrap', fontWeight: 700 }}>{euro(parseFloat(l.price) * l.quantity)}</span>
              </div>
              {(l.noteOpen || l.notes) && (
                <input
                  className="picker-line-note"
                  placeholder="Note cuisine (cuisson, sans oignons…)"
                  value={l.notes}
                  onChange={(e) => setLines(prev => prev.map(x => x.uid === l.uid ? { ...x, notes: e.target.value } : x))}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {error && <p className="staff-error" style={{ padding: '0 16px' }}>{error}</p>}

      <div className="picker-foot">
        <button className="staff-btn staff-btn-or" disabled={count === 0 || busy} onClick={send}>
          {busy ? 'Envoi…' : `Envoyer en cuisine (${count}) — ${euro(total)}`}
        </button>
      </div>

      {optionsFor && (
        <div className="staff-modal-overlay" onClick={() => setOptionsFor(null)}>
          <div className="staff-modal" onClick={(e) => e.stopPropagation()}>
            <h2>{optionsFor.name}</h2>
            <div className="pay-methods">
              <button className="pay-method" onClick={() => add(optionsFor)}>
                <span style={{ marginRight: 'auto' }}>Nature</span>
                <strong>{euro(optionsFor.price)}</strong>
              </button>
              {optionsFor.options.map(o => (
                <button key={o.name} className="pay-method" onClick={() => add(optionsFor, o)}>
                  <span style={{ marginRight: 'auto' }}>{o.name}</span>
                  <strong>{euro(parseFloat(optionsFor.price) + (parseFloat(o.price) || 0))}</strong>
                </button>
              ))}
            </div>
            <button className="staff-btn-ghost" onClick={() => setOptionsFor(null)}>Annuler</button>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Détail d'une addition (envois, total, actions) ---

export function TabDetailPanel({ tab, onClose, onAddOrder, onPay, onCancel, canOrder = true, tally = false }) {
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [serving, setServing] = useState(null);
  const [tallying, setTallying] = useState(null);
  const [busy, setBusy] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [freeTables, setFreeTables] = useState(null);

  if (!tab) return null;
  const isOpen = tab.status === 'open';

  // Déplacer l'addition vers une autre table libre
  const openMove = async () => {
    setMoveOpen(true);
    setFreeTables(null);
    try {
      const res = await staffFetch('/service/overview');
      const body = await res.json();
      setFreeTables(body.tables.filter(t => t.id !== tab.diningTableId && !t.tab && t.active !== false));
    } catch { setFreeTables([]); }
  };

  const doMove = async (targetTableId) => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await staffFetch(`/service/tab/${tab.id}/move`, {
        method: 'POST', body: JSON.stringify({ targetTableId })
      });
      if (!res.ok) { const b = await res.json().catch(() => ({})); alert(b.message || 'Déplacement impossible'); }
      setMoveOpen(false);
    } finally { setBusy(false); }
  };

  const cancelOneOrder = async (orderId) => {
    if (busy || !window.confirm('Annuler cet envoi ? Les autres envois de la table sont conservés.')) return;
    setBusy(true);
    try {
      await staffFetch(`/service/tab/${tab.id}/orders/${orderId}/cancel`, { method: 'POST' });
    } finally { setBusy(false); }
  };

  const removeOneItem = async (itemId) => {
    if (busy) return;
    setBusy(true);
    try {
      await staffFetch(`/service/tab/${tab.id}/items/${itemId}`, { method: 'DELETE' });
    } finally { setBusy(false); }
  };

  const activeItems = tab.orders
    .filter(o => o.status !== 'cancelled')
    .flatMap(o => o.items || []);
  const talliedCount = activeItems.filter(i => i.tallied).length;

  // Pointage : on barre la ligne au fur et à mesure de sa saisie sur la
  // caisse principale. Persistant, partagé entre les appareils (socket).
  const toggleTally = async (item) => {
    if (!tally || tallying) return;
    setTallying(item.id);
    try {
      await staffFetch(`/service/tab/${tab.id}/items/${item.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ tallied: !item.tallied })
      });
    } finally {
      setTallying(null);
    }
  };

  // « Servi » : c'est le service qui clôt un envoi prêt, pas la cuisine
  const markServed = async (order) => {
    if (serving) return;
    setServing(order.id);
    try {
      await staffFetch(`/orders/${order.id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'completed' })
      });
      // le socket tab-updated rafraîchit le panneau via le parent
    } finally {
      setServing(null);
    }
  };

  return (
    <div className="tabdetail-overlay" onClick={onClose}>
      <div className="tabdetail" onClick={(e) => e.stopPropagation()}>
        <div className="tabdetail-head">
          <h2>Table {tab.tableName}</h2>
          {tab.guestCount ? <span className="staff-topbar-who">{tab.guestCount} couverts</span> : null}
          <span className={`st-pill ${tab.status === 'open' ? 'st-ready' : 'st-completed'}`}>
            {tab.status === 'open' ? 'Ouverte' : tab.status === 'paid' ? 'Encaissée' : 'Annulée'}
          </span>
          <button className="tabdetail-close" onClick={onClose}>✕</button>
        </div>

        <div className="tabdetail-body">
          {tab.note && <p style={{ color: '#ffce7a', marginBottom: 10 }}>📌 {tab.note}</p>}

          {tab.orders.length === 0 && (
            <p className="caisse-empty">Aucune commande pour le moment.</p>
          )}

          {tab.orders.map((o, idx) => {
            const st = ORDER_STATUS[o.status] || ORDER_STATUS.pending;
            return (
              <div key={o.id} className="tabdetail-round">
                <div className="tabdetail-round-head">
                  <strong>Envoi {idx + 1}</strong>
                  <span>{timeHM(o.createdAt)}</span>
                  {o.guestLabel && <span>· {o.guestLabel}</span>}
                  <span className={`st-pill ${st.class}`}>{st.label}</span>
                  {isOpen && o.status === 'ready' && (
                    <button
                      className="staff-btn staff-btn-or tabdetail-serve"
                      disabled={serving === o.id}
                      onClick={() => markServed(o)}
                    >
                      Servi ✓
                    </button>
                  )}
                  {isOpen && o.status !== 'cancelled' && (
                    <button className="tabdetail-cancel-order" disabled={busy} onClick={() => cancelOneOrder(o.id)} title="Annuler cet envoi">
                      Annuler l'envoi
                    </button>
                  )}
                </div>
                {(o.items || []).map(it => (
                  <div
                    key={it.id}
                    className={`tabdetail-item ${tally ? 'tallyable' : ''} ${it.tallied ? 'tallied' : ''}`}
                    onClick={() => tally && o.status !== 'cancelled' && toggleTally(it)}
                    role={tally ? 'button' : undefined}
                  >
                    {tally && <span className="tally-box">{it.tallied ? '✓' : ''}</span>}
                    <span className="kds-qty">{it.quantity}×</span>
                    <span>
                      {it.name}
                      {it.notes && <span className="kds-note">{it.notes}</span>}
                    </span>
                    <span className="tabdetail-item-price">
                      {euro(parseFloat(it.price) * it.quantity)}
                    </span>
                    {isOpen && o.status !== 'cancelled' && (
                      <button className="tabdetail-item-remove" disabled={busy}
                        onClick={(e) => { e.stopPropagation(); removeOneItem(it.id); }}
                        title="Retirer cet article">✕</button>
                    )}
                  </div>
                ))}
              </div>
            );
          })}
        </div>

        <div className="tabdetail-foot">
          <div className="tabdetail-total">
            <span>
              {tab.orders.filter(o => o.status !== 'cancelled').length} envoi(s)
              {tally && activeItems.length > 0 && (
                <span className={`tally-progress ${talliedCount === activeItems.length ? 'done' : ''}`}>
                  {' '}· {talliedCount}/{activeItems.length} saisie{talliedCount > 1 ? 's' : ''} en caisse
                </span>
              )}
            </span>
            <strong>{euro(tab.total)}</strong>
          </div>

          {isOpen && (
            <>
              <div className="tabdetail-actions">
                {canOrder && (
                  <button className="staff-btn staff-btn-dark" onClick={onAddOrder}>+ Commande</button>
                )}
                <button
                  className="staff-btn staff-btn-or"
                  style={canOrder ? {} : { gridColumn: '1 / -1' }}
                  onClick={onPay}
                >
                  Encaisser {euro(tab.total)}
                </button>
              </div>
              <button className="staff-btn-ghost" onClick={openMove}>
                ↔ Déplacer vers une autre table
              </button>
              {!confirmCancel ? (
                <button className="staff-btn-ghost" onClick={() => setConfirmCancel(true)}>
                  Annuler cette table…
                </button>
              ) : (
                <div className="tabdetail-actions" style={{ marginTop: 10 }}>
                  <button className="staff-btn staff-btn-dark" onClick={() => setConfirmCancel(false)}>Non, garder</button>
                  <button className="staff-btn staff-btn-danger" onClick={onCancel}>Oui, annuler tout</button>
                </div>
              )}
            </>
          )}
          {tab.status === 'paid' && (
            <p style={{ color: '#4caf7d', fontWeight: 700, textAlign: 'center' }}>
              Encaissée ✓
            </p>
          )}
        </div>

        {moveOpen && (
          <div className="staff-modal-overlay" onClick={() => setMoveOpen(false)}>
            <div className="staff-modal" onClick={(e) => e.stopPropagation()}>
              <h2>Déplacer la table {tab.tableName} vers…</h2>
              {freeTables === null ? (
                <p className="staff-topbar-who">Chargement…</p>
              ) : freeTables.length === 0 ? (
                <p className="staff-topbar-who">Aucune autre table libre pour le moment.</p>
              ) : (
                <div className="move-tables">
                  {freeTables.map(t => (
                    <button key={t.id} className="staff-btn staff-btn-dark" disabled={busy} onClick={() => doMove(t.id)}>
                      Table {t.name}
                    </button>
                  ))}
                </div>
              )}
              <button className="staff-btn-ghost" onClick={() => setMoveOpen(false)}>Annuler</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Helper commun : envoie une commande sur une addition et renvoie le détail
export async function sendOrderToTab(tabId, items) {
  const res = await staffFetch(`/service/tab/${tabId}/orders`, {
    method: 'POST',
    body: JSON.stringify({ items })
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(body.message || (body.errors && body.errors[0] && body.errors[0].msg) || 'Envoi impossible');
  }
  return body;
}
